import { useEffect, useMemo, useState } from 'react';
import {
  BESCHIKBARE_FONTEN,
  frameNaarSeconde,
  secondeNaarFrame,
  werkItemBij,
  type AfbeeldingItem,
  type AudioItem,
  type ProjectModel,
  type TekstItem,
  type TimelineItem,
  type VideoItem,
} from '@video-studio/shared';
import type { MediaDto } from '../../api/client';
import type { useEditor } from '../../state/useEditor';

/**
 * Het eigenschappenpaneel rechts.
 *
 * Toont alleen wat bij het geselecteerde item hoort. Alle wijzigingen lopen via
 * `editor.bewerk`, dus elke aanpassing is ongedaan te maken en wordt
 * automatisch opgeslagen.
 */
export interface EigenschappenpaneelProps {
  editor: ReturnType<typeof useEditor>;
  project: ProjectModel;
  item: TimelineItem | null;
  mediaMap: Map<string, MediaDto>;
  opFrame: number;
}

export function Eigenschappenpaneel({ editor, project, item, mediaMap, opFrame }: EigenschappenpaneelProps) {
  if (!item) {
    return (
      <aside className="paneel paneel--rechts">
        <div className="paneel__leeg">
          <h3>Eigenschappen</h3>
          <p className="paneel__hint">
            Selecteer een laag op de tijdlijn om de eigenschappen te bewerken.
          </p>
          <ProjectEigenschappen editor={editor} project={project} />
        </div>
      </aside>
    );
  }

  const media = 'assetId' in item ? mediaMap.get(item.assetId) : undefined;

  return (
    <aside className="paneel paneel--rechts">
      <div className="paneel__kop">
        <h3>{item.naam || item.soort}</h3>
        <span className="paneel__soort">{item.soort}</span>
      </div>

      <div className="paneel__inhoud">
        <AlgemeneEigenschappen editor={editor} project={project} item={item} />
        {item.soort === 'video' && (
          <VideoEigenschappen editor={editor} project={project} item={item} media={media} />
        )}
        {item.soort === 'afbeelding' && (
          <AfbeeldingEigenschappen editor={editor} item={item} media={media} />
        )}
        {item.soort === 'tekst' && <TekstEigenschappen editor={editor} item={item} />}
        {item.soort === 'audio' && (
          <AudioEigenschappen editor={editor} project={project} item={item} media={media} />
        )}
      </div>
    </aside>
  );
}

function wijzig(
  editor: ReturnType<typeof useEditor>,
  itemId: string,
  wijziging: Partial<TimelineItem>,
  beschrijving: string,
) {
  editor.bewerk((p) => werkItemBij(p, itemId, wijziging), beschrijving);
}

function AlgemeneEigenschappen({
  editor,
  project,
  item,
}: {
  editor: ReturnType<typeof useEditor>;
  project: ProjectModel;
  item: TimelineItem;
}) {
  const fps = project.fps;
  return (
    <section className="eigenschappen__groep">
      <h4>Positie en tijd</h4>
      <div className="rij">
        <label className="veld veld--kort">
          <span>Begin (s)</span>
          <input
            type="number"
            step="0.1"
            min={0}
            value={frameNaarSeconde(item.startFrame, fps).toFixed(2)}
            onChange={(e) =>
              wijzig(editor, item.id, { startFrame: secondeNaarFrame(Number(e.target.value), fps) }, 'Beginpunt wijzigen')
            }
          />
        </label>
        <label className="veld veld--kort">
          <span>Duur (s)</span>
          <input
            type="number"
            step="0.1"
            min={1 / fps}
            value={frameNaarSeconde(item.duurInFrames, fps).toFixed(2)}
            onChange={(e) =>
              wijzig(
                editor,
                item.id,
                { duurInFrames: Math.max(1, secondeNaarFrame(Number(e.target.value), fps)) },
                'Duur wijzigen',
              )
            }
          />
        </label>
      </div>
      <label className="veld">
        <span>Naam</span>
        <input
          type="text"
          value={item.naam}
          onChange={(e) => wijzig(editor, item.id, { naam: e.target.value }, 'Naam wijzigen')}
        />
      </label>
      <div className="rij">
        <label className="veld veld--vink">
          <input
            type="checkbox"
            checked={item.verborgen}
            onChange={(e) => wijzig(editor, item.id, { verborgen: e.target.checked }, 'Laag verbergen')}
          />
          <span>Verborgen</span>
        </label>
        <label className="veld veld--vink">
          <input
            type="checkbox"
            checked={item.vergrendeld}
            onChange={(e) => wijzig(editor, item.id, { vergrendeld: e.target.checked }, 'Laag vergrendelen')}
          />
          <span>Vergrendeld</span>
        </label>
      </div>
    </section>
  );
}

function VideoEigenschappen({
  editor,
  project,
  item,
  media,
}: {
  editor: ReturnType<typeof useEditor>;
  project: ProjectModel;
  item: VideoItem;
  media?: MediaDto;
}) {
  const fps = project.fps;
  const maxBronFrames = media?.inspectie?.duurSeconden
    ? secondeNaarFrame(media.inspectie.duurSeconden, fps)
    : null;

  return (
    <>
      <section className="eigenschappen__groep">
        <h4>Beeld</h4>
        <label className="veld">
          <span>Passend maken</span>
          <select
            value={item.passing}
            onChange={(e) => wijzig(editor, item.id, { passing: e.target.value as VideoItem['passing'] }, 'Passend maken')}
          >
            <option value="bevatten">Bevatten (zwarte randen, niets valt weg)</option>
            <option value="bijsnijden">Bijsnijden (vult het kader)</option>
            <option value="uitrekken">Uitrekken (vervormt)</option>
          </select>
        </label>
        <label className="veld">
          <span>Snelheid: {item.snelheid.toFixed(2)}×</span>
          <input
            type="range"
            min={0.25}
            max={4}
            step={0.05}
            value={item.snelheid}
            onChange={(e) => wijzig(editor, item.id, { snelheid: Number(e.target.value) }, 'Snelheid wijzigen')}
          />
        </label>
      </section>

      <section className="eigenschappen__groep">
        <h4>Knippen in de bron</h4>
        <label className="veld">
          <span>Startpunt in de bron: {frameNaarSeconde(item.bronStartFrame, fps).toFixed(2)} s</span>
          <input
            type="range"
            min={0}
            max={Math.max(0, (maxBronFrames ?? item.bronStartFrame + item.duurInFrames) - 1)}
            value={item.bronStartFrame}
            onChange={(e) =>
              wijzig(editor, item.id, { bronStartFrame: Number(e.target.value) }, 'Knippen in de bron')
            }
          />
        </label>
        <p className="paneel__hint">
          De clip houdt dezelfde lengte op de tijdlijn; alleen het stuk uit de bron verschuift.
        </p>
      </section>

      <section className="eigenschappen__groep">
        <h4>Geluid</h4>
        <label className="veld veld--vink">
          <input
            type="checkbox"
            checked={item.gedempt}
            onChange={(e) => wijzig(editor, item.id, { gedempt: e.target.checked }, 'Clip dempen')}
          />
          <span>Deze clip dempen</span>
        </label>
        <label className="veld">
          <span>Volume: {Math.round(item.volume * 100)}%</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={item.volume}
            disabled={item.gedempt}
            onChange={(e) => wijzig(editor, item.id, { volume: Number(e.target.value) }, 'Volume wijzigen')}
          />
        </label>
        <div className="rij">
          <label className="veld veld--kort">
            <span>Fade in (s)</span>
            <input
              type="number"
              step="0.1"
              min={0}
              value={frameNaarSeconde(item.fadeInFrames, fps).toFixed(2)}
              onChange={(e) =>
                wijzig(editor, item.id, { fadeInFrames: secondeNaarFrame(Number(e.target.value), fps) }, 'Fade in')
              }
            />
          </label>
          <label className="veld veld--kort">
            <span>Fade uit (s)</span>
            <input
              type="number"
              step="0.1"
              min={0}
              value={frameNaarSeconde(item.fadeOutFrames, fps).toFixed(2)}
              onChange={(e) =>
                wijzig(editor, item.id, { fadeOutFrames: secondeNaarFrame(Number(e.target.value), fps) }, 'Fade uit')
              }
            />
          </label>
        </div>
      </section>

      <section className="eigenschappen__groep">
        <h4>Overgang aan het begin</h4>
        <label className="veld">
          <span>Soort</span>
          <select
            value={item.overgangIn}
            onChange={(e) =>
              wijzig(editor, item.id, { overgangIn: e.target.value as VideoItem['overgangIn'] }, 'Overgang wijzigen')
            }
          >
            <option value="geen">Geen</option>
            <option value="vervagen">Vervagen vanuit de vorige clip</option>
            <option value="zwart">Vanuit zwart</option>
          </select>
        </label>
        {item.overgangIn !== 'geen' && (
          <label className="veld">
            <span>Duur overgang: {frameNaarSeconde(item.overgangDuurInFrames, fps).toFixed(2)} s</span>
            <input
              type="range"
              min={0}
              max={Math.max(2, Math.round(fps * 2))}
              value={item.overgangDuurInFrames}
              onChange={(e) =>
                wijzig(editor, item.id, { overgangDuurInFrames: Number(e.target.value) }, 'Overgangsduur')
              }
            />
          </label>
        )}
      </section>
    </>
  );
}

function AfbeeldingEigenschappen({
  editor,
  item,
  media,
}: {
  editor: ReturnType<typeof useEditor>;
  item: AfbeeldingItem;
  media?: MediaDto;
}) {
  return (
    <section className="eigenschappen__groep">
      <h4>Beeld</h4>
      {media && (
        <p className="paneel__hint">
          {media.inspectie?.breedte ?? '?'}×{media.inspectie?.hoogte ?? '?'} · {media.naam}
        </p>
      )}
      <label className="veld">
        <span>Passend maken</span>
        <select
          value={item.passing}
          onChange={(e) =>
            wijzig(editor, item.id, { passing: e.target.value as AfbeeldingItem['passing'] }, 'Passend maken')
          }
        >
          <option value="bevatten">Bevatten (past binnen het kader)</option>
          <option value="bijsnijden">Bijsnijden (vult het kader)</option>
          <option value="uitrekken">Uitrekken (vervormt)</option>
        </select>
      </label>
      <div className="rij">
        <label className="veld veld--kort">
          <span>X {Math.round(item.x * 100)}%</span>
          <input
            type="range"
            min={-0.5}
            max={1.5}
            step={0.005}
            value={item.x}
            onChange={(e) => wijzig(editor, item.id, { x: Number(e.target.value) }, 'Positie wijzigen')}
          />
        </label>
        <label className="veld veld--kort">
          <span>Y {Math.round(item.y * 100)}%</span>
          <input
            type="range"
            min={-0.5}
            max={1.5}
            step={0.005}
            value={item.y}
            onChange={(e) => wijzig(editor, item.id, { y: Number(e.target.value) }, 'Positie wijzigen')}
          />
        </label>
      </div>
      <label className="veld">
        <span>Schaal: {Math.round(item.schaal * 100)}%</span>
        <input
          type="range"
          min={0.05}
          max={3}
          step={0.01}
          value={item.schaal}
          onChange={(e) => wijzig(editor, item.id, { schaal: Number(e.target.value) }, 'Schaal wijzigen')}
        />
      </label>
      <label className="veld">
        <span>Rotatie: {item.rotatie}°</span>
        <input
          type="range"
          min={-180}
          max={180}
          step={1}
          value={item.rotatie}
          onChange={(e) => wijzig(editor, item.id, { rotatie: Number(e.target.value) }, 'Rotatie wijzigen')}
        />
      </label>
      <label className="veld">
        <span>Dekking: {Math.round(item.dekking * 100)}%</span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={item.dekking}
          onChange={(e) => wijzig(editor, item.id, { dekking: Number(e.target.value) }, 'Dekking wijzigen')}
        />
      </label>
    </section>
  );
}

function TekstEigenschappen({ editor, item }: { editor: ReturnType<typeof useEditor>; item: TekstItem }) {
  const [lokaleTekst, setLokaleTekst] = useState(item.tekst);

  // Als een ander item geselecteerd wordt, neemt het veld de nieuwe tekst over.
  useEffect(() => {
    setLokaleTekst(item.tekst);
  }, [item.id, item.tekst]);

  return (
    <>
      <section className="eigenschappen__groep">
        <h4>Tekst</h4>
        <label className="veld">
          <span>Inhoud</span>
          <textarea
            rows={3}
            value={lokaleTekst}
            onChange={(e) => {
              setLokaleTekst(e.target.value);
              wijzig(editor, item.id, { tekst: e.target.value }, 'Tekst wijzigen');
            }}
          />
        </label>
        <label className="veld">
          <span>Lettertype</span>
          <select
            value={item.fontFamily}
            onChange={(e) => wijzig(editor, item.id, { fontFamily: e.target.value }, 'Lettertype wijzigen')}
          >
            {BESCHIKBARE_FONTEN.map((font) => (
              <option key={font.id} value={font.id}>
                {font.label}
              </option>
            ))}
          </select>
        </label>
        <div className="rij">
          <label className="veld veld--kort">
            <span>Grootte</span>
            <input
              type="number"
              min={8}
              max={400}
              value={item.fontSize}
              onChange={(e) => wijzig(editor, item.id, { fontSize: Number(e.target.value) }, 'Grootte wijzigen')}
            />
          </label>
          <label className="veld veld--kort">
            <span>Dikte</span>
            <select
              value={item.fontWeight}
              onChange={(e) => wijzig(editor, item.id, { fontWeight: Number(e.target.value) }, 'Dikte wijzigen')}
            >
              {[300, 400, 500, 600, 700, 800, 900].map((w) => (
                <option key={w} value={w}>
                  {w}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="eigenschappen__groep">
        <h4>Opmaak</h4>
        <div className="rij">
          <label className="veld veld--kort">
            <span>Kleur</span>
            <input
              type="color"
              value={item.kleur}
              onChange={(e) => wijzig(editor, item.id, { kleur: e.target.value }, 'Kleur wijzigen')}
            />
          </label>
          <label className="veld veld--kort">
            <span>Achtergrond</span>
            <input
              type="color"
              value={item.achtergrondKleur === 'transparant' ? '#000000' : item.achtergrondKleur}
              onChange={(e) => wijzig(editor, item.id, { achtergrondKleur: e.target.value }, 'Achtergrond wijzigen')}
            />
          </label>
        </div>
        <label className="veld veld--vink">
          <input
            type="checkbox"
            checked={item.achtergrondKleur !== 'transparant'}
            onChange={(e) =>
              wijzig(
                editor,
                item.id,
                { achtergrondKleur: e.target.checked ? '#000000aa' : 'transparant' },
                'Achtergrond aan/uit',
              )
            }
          />
          <span>Achtergrondvlak gebruiken</span>
        </label>
        <label className="veld veld--vink">
          <input
            type="checkbox"
            checked={item.schaduw}
            onChange={(e) => wijzig(editor, item.id, { schaduw: e.target.checked }, 'Schaduw aan/uit')}
          />
          <span>Tekstschaduw voor leesbaarheid</span>
        </label>
        <div className="veld">
          <span>Uitlijning</span>
          <div className="keuzeknoppen">
            {(['links', 'midden', 'rechts'] as const).map((uitlijning) => (
              <button
                key={uitlijning}
                type="button"
                className={`knop knop--klein ${item.uitlijning === uitlijning ? 'knop--actief' : ''}`}
                onClick={() => wijzig(editor, item.id, { uitlijning }, 'Uitlijning wijzigen')}
              >
                {uitlijning}
              </button>
            ))}
          </div>
        </div>
        <label className="veld">
          <span>Animatie</span>
          <select
            value={item.animatie}
            onChange={(e) =>
              wijzig(editor, item.id, { animatie: e.target.value as TekstItem['animatie'] }, 'Animatie wijzigen')
            }
          >
            <option value="geen">Geen</option>
            <option value="vervagen">Vervagen</option>
            <option value="omhoog">Omhoog komen</option>
          </select>
        </label>
      </section>

      <section className="eigenschappen__groep">
        <h4>Positie</h4>
        <div className="rij">
          <label className="veld veld--kort">
            <span>X {Math.round(item.x * 100)}%</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.005}
              value={item.x}
              onChange={(e) => wijzig(editor, item.id, { x: Number(e.target.value) }, 'Positie wijzigen')}
            />
          </label>
          <label className="veld veld--kort">
            <span>Y {Math.round(item.y * 100)}%</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.005}
              value={item.y}
              onChange={(e) => wijzig(editor, item.id, { y: Number(e.target.value) }, 'Positie wijzigen')}
            />
          </label>
        </div>
        <div className="keuzeknoppen">
          <button
            type="button"
            className="knop knop--klein"
            onClick={() => wijzig(editor, item.id, { x: 0.15, y: 0.1 }, 'Rechtsboven plaatsen')}
            title="Logo of tekst rechtsboven"
          >
            Rechtsboven
          </button>
          <button
            type="button"
            className="knop knop--klein"
            onClick={() => wijzig(editor, item.id, { x: 0.5, y: 0.5 }, 'Centreren')}
          >
            Midden
          </button>
          <button
            type="button"
            className="knop knop--klein"
            onClick={() => wijzig(editor, item.id, { x: 0.5, y: 0.88 }, 'Onderaan plaatsen')}
          >
            Onderaan
          </button>
        </div>
      </section>
    </>
  );
}

function AudioEigenschappen({
  editor,
  project,
  item,
  media,
}: {
  editor: ReturnType<typeof useEditor>;
  project: ProjectModel;
  item: AudioItem;
  media?: MediaDto;
}) {
  const fps = project.fps;
  return (
    <>
      <section className="eigenschappen__groep">
        <h4>Bron</h4>
        <p className="paneel__hint">
          {media?.naam ?? 'Onbekende audio'}
          {media?.inspectie?.duurSeconden ? ` · ${media.inspectie.duurSeconden.toFixed(1)} s` : ''}
        </p>
      </section>
      <section className="eigenschappen__groep">
        <h4>Volume en fade</h4>
        <label className="veld veld--vink">
          <input
            type="checkbox"
            checked={item.gedempt}
            onChange={(e) => wijzig(editor, item.id, { gedempt: e.target.checked }, 'Audiospoor dempen')}
          />
          <span>Dempen</span>
        </label>
        <label className="veld">
          <span>Volume: {Math.round(item.volume * 100)}%</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={item.volume}
            disabled={item.gedempt}
            onChange={(e) => wijzig(editor, item.id, { volume: Number(e.target.value) }, 'Volume wijzigen')}
          />
        </label>
        <div className="rij">
          <label className="veld veld--kort">
            <span>Fade in (s)</span>
            <input
              type="number"
              step="0.1"
              min={0}
              value={frameNaarSeconde(item.fadeInFrames, fps).toFixed(2)}
              onChange={(e) =>
                wijzig(editor, item.id, { fadeInFrames: secondeNaarFrame(Number(e.target.value), fps) }, 'Fade in')
              }
            />
          </label>
          <label className="veld veld--kort">
            <span>Fade uit (s)</span>
            <input
              type="number"
              step="0.1"
              min={0}
              value={frameNaarSeconde(item.fadeOutFrames, fps).toFixed(2)}
              onChange={(e) =>
                wijzig(editor, item.id, { fadeOutFrames: secondeNaarFrame(Number(e.target.value), fps) }, 'Fade uit')
              }
            />
          </label>
        </div>
      </section>
    </>
  );
}

function ProjectEigenschappen({ editor, project }: { editor: ReturnType<typeof useEditor>; project: ProjectModel }) {
  const duur = useMemo(() => (editor.duurInFrames / project.fps).toFixed(2), [editor.duurInFrames, project.fps]);
  const aantalItems = project.tracks.reduce((som, t) => som + t.items.length, 0);

  return (
    <section className="eigenschappen__groep">
      <h4>Project</h4>
      <dl className="gegevenslijst">
        <dt>Naam</dt>
        <dd>{project.naam}</dd>
        <dt>Formaat</dt>
        <dd>
          {project.breedte}×{project.hoogte} ({project.beeldformaat})
        </dd>
        <dt>Frames per seconde</dt>
        <dd>{project.fps}</dd>
        <dt>Duur</dt>
        <dd>{duur} s</dd>
        <dt>Lagen</dt>
        <dd>{aantalItems}</dd>
      </dl>
      <label className="veld">
        <span>Achtergrondkleur</span>
        <input
          type="color"
          value={project.achtergrond.kleur}
          onChange={(e) =>
            editor.bewerk((p) => ({ ...p, achtergrond: { ...p.achtergrond, kleur: e.target.value } }), 'Achtergrondkleur')
          }
        />
      </label>
    </section>
  );
}
